import { Box, Grid, Typography, FormControlLabel, Switch } from '@material-ui/core';
import {
  Button,
  ConfirmationButton,
  StandardDialog,
} from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useDocumentStore } from '../../../hooks/useDocumentStore';
import { TitleCompanyBankInformationForm } from './titleCompanyBankInformationForm';
import { FileUploadDownloadArea } from '../common/fileUploadDownloadArea';
import { Document } from '@roc/feature-types';
import { CustomTaskDialog } from '@roc/feature-general-tasks';

interface Props {
  open?: boolean;
  onClose?: () => void;
  document: Document;
}

export const TitleCompanyBankInformationDialog = observer((props: Props) => {
  const { open, onClose, document } = props;
  const taskId = document?.taskId;
  const { documentStore } = useDocumentStore();
  const { documentFormStore, globalStore } = documentStore;
  const { titleCompanyBankInformationStore } = documentFormStore;
  const { editEnabled } = titleCompanyBankInformationStore;
  const isTitleCompanyBankInfoReviewAllowed = !!globalStore.userFeatures?.closingEntitlements.isTitleCompanyBankInfoReviewAllowed;

  useEffect(() => {
    if (open) {
      titleCompanyBankInformationStore.initialize();
      if (taskId) {
        titleCompanyBankInformationStore.fetchTaskDocument(taskId);
      }
    }
  }, [open]);

  const onReject = () => {
    titleCompanyBankInformationStore.rejectBankInformation();
  }

  const onApprove = () => {
    titleCompanyBankInformationStore.approveBankInformation();
  }

  const onSave = () => {
    titleCompanyBankInformationStore.saveWireInformation();
  }

  const handleClose = () => {
    if (isTitleCompanyBankInfoReviewAllowed && editEnabled) {
      titleCompanyBankInformationStore.setEditEnabled(false);
    } else {
      onClose();
    }
  }

  const renderTitle = () => {
    return (
      <Box display={'flex'} alignItems={'end'}>
        <Typography variant='h4'>Title Company Bank Information</Typography>
        {isTitleCompanyBankInfoReviewAllowed && (
          <FormControlLabel
            control={<Switch checked={editEnabled} onChange={() => { titleCompanyBankInformationStore.setEditEnabled(!editEnabled) }} color="primary" />}
            label='Edit'
            labelPlacement='start'
          />
        )}
      </Box>
    );
  }

  const renderActions = () => {
    const buttons = [
      (<Grid item>
        <Button
          onClick={onSave}
          color="primary"
          variant="contained"
          testId="save"
        >
          Save
        </Button>
      </Grid>),
      (<Grid item>
        <Button
          onClick={handleClose}
          color="primary"
          variant="outlined"
          testId="cancel"
        >
          Cancel
        </Button>
      </Grid>),
    ];

    if (isTitleCompanyBankInfoReviewAllowed) {
      buttons.push(
        (<Grid item>
          <ConfirmationButton
            confirmDialogProps={{
              body: 'Are you sure you want to reject this bank information?',
            }}
            onClick={onReject}
            color="primary"
            variant="contained"
            testId="confirm"
          >
            Reject
          </ConfirmationButton>
        </Grid>),
        (<Grid item>
          <ConfirmationButton
            confirmDialogProps={{
              body: 'Are you sure you want to approve this bank information?',
            }}
            onClick={onApprove}
            color="primary"
            variant="contained"
            testId="confirm"
          >
            Approve
          </ConfirmationButton>
        </Grid>)
      );
    }

    return buttons;
  }

  return (
    <>
      {taskId ? (
        <CustomTaskDialog
          open={open}
          title={'Title Bank Company'}
          maxWidth="lg"
          handleClose={onClose}
          dialogContent={
            <>
              <Box marginBottom={'24px'}>
                {renderTitle()}
              </Box>
              <TitleCompanyBankInformationForm titleCompanyBankInformationStore={titleCompanyBankInformationStore} />
              {titleCompanyBankInformationStore.wireInstructionsDocumentName && (
                <FileUploadDownloadArea
                  file={null}
                  onChange={() => {}}
                  readOnlyFileName={titleCompanyBankInformationStore.wireInstructionsDocumentName}
                  onDownload={() => titleCompanyBankInformationStore.downloadWireInstructionsDocument(taskId)}
                  disableDownload={false}
                />
              )}
            </>
          }
          dialogActions={
            <Grid container spacing={1} justifyContent="flex-end">
              {renderActions()}
            </Grid>
          }
          taskId={taskId}
        />
      ) : (
        <StandardDialog
          open={open}
          title={renderTitle()}
          maxWidth="lg"
          handleClose={onClose}
          dialogContent={
            <>
              <TitleCompanyBankInformationForm titleCompanyBankInformationStore={titleCompanyBankInformationStore} />
              {titleCompanyBankInformationStore.wireInstructionsDocumentName && (
                <FileUploadDownloadArea
                  file={null}
                  onChange={() => {}}
                  readOnlyFileName={titleCompanyBankInformationStore.wireInstructionsDocumentName}
                  onDownload={() => titleCompanyBankInformationStore.downloadWireInstructionsDocument(taskId)}
                  disableDownload={false}
                />
              )}
            </>
          }
          dialogActions={
            <Grid container spacing={1} justifyContent="flex-end">
              {renderActions()}
            </Grid>
          }
        />
      )}
    </>
  );
});
